export const GOVERNING_FEEDS = ["badging", "headcount", "leases", "roles"];
export const OTHER_FEEDS = ["logins", "fx", "calendar"];
const STATUSES = ["Current", "Stale", "Partial", "Error"];

export function localGreeting(displayName, now = new Date(), locale, timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone) {
  const firstName = typeof displayName === "string" ? displayName.trim().split(/\s+/)[0] : "";
  const hour = Number(new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric", hourCycle: "h23" }).format(now));
  const period = hour >= 5 && hour < 12 ? "morning" : hour >= 12 && hour < 17 ? "afternoon" : "evening";
  return {
    greeting: firstName ? `Good ${period}, ${firstName}` : "Good morning",
    date: new Intl.DateTimeFormat(locale, { timeZone, weekday: "long", month: "short", day: "numeric" }).format(now),
  };
}

// Validate the returned feed contract, never reconstruct missing source statuses.
export function coverageFeeds(response) {
  if (!Array.isArray(response?.feeds) || typeof response.asOf !== "string"
    || !response.asOf.includes("T") || !Number.isFinite(Date.parse(response.asOf))) return null;
  const ids = [...GOVERNING_FEEDS, ...OTHER_FEEDS];
  const rows = ids.map((id) => response.feeds.filter((feed) => feed?.id === id));
  if (rows.some((matches) => matches.length !== 1)) return null;
  const feeds = rows.map(([feed]) => feed);
  const timestamp = (value) => value === null || (typeof value === "string" && value.includes("T") && Number.isFinite(Date.parse(value)));
  if (feeds.some((feed) => !STATUSES.includes(feed.status)
    || !["label", "source", "cadence", "notes"].every((key) => typeof feed[key] === "string")
    || !timestamp(feed.lastSuccessAt) || !timestamp(feed.lastAttemptAt)
    || !Array.isArray(feed.evidence)
    || feed.evidence.some((item) => !item || typeof item.label !== "string"
      || !(item.value === null || typeof item.value === "string")))) return null;
  return feeds;
}

export function freshnessSummary(feeds) {
  const governing = GOVERNING_FEEDS.map((id) => feeds?.find((feed) => feed.id === id));
  if (governing.some((feed) => !feed || !STATUSES.includes(feed.status))) return null;
  const errors = governing.filter((feed) => feed.status === "Error").length;
  const stale = governing.filter((feed) => ["Stale", "Partial"].includes(feed.status)).length;
  if (errors) return { tone: "error", label: `${errors} source${errors === 1 ? "" : "s"} failing` };
  if (stale) return { tone: "stale", label: `${stale} source${stale === 1 ? "" : "s"} stale` };
  return { tone: "current", label: "Data current" };
}

export function timestampAge(value, now = new Date()) {
  if (typeof value !== "string" || !value.includes("T")) return null;
  const time = Date.parse(value);
  if (!Number.isFinite(time)) return null;
  const seconds = Math.round((now.getTime() - time) / 1000);
  const magnitude = Math.abs(seconds);
  if (magnitude < 60) return seconds < 0 ? "in under 1m" : "just now";
  const amount = magnitude < 3600 ? `${Math.floor(magnitude / 60)}m`
    : magnitude < 86400 ? `${Math.floor(magnitude / 3600)}h` : `${Math.floor(magnitude / 86400)}d`;
  return seconds < 0 ? `in ${amount}` : `${amount} ago`;
}

export function evidenceValue(value, now = new Date(), locale) {
  if (!value) return "Unavailable";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const date = new Date(`${value}T00:00:00Z`);
    if (!Number.isFinite(date.getTime()) || date.toISOString().slice(0, 10) !== value) return "Unavailable";
    return new Intl.DateTimeFormat(locale, { year: "numeric", month: "short", day: "numeric", timeZone: "UTC" }).format(date);
  }
  const age = timestampAge(value, now);
  return age ? `${new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value))} · ${age}` : value;
}
